import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { WorkMode } from '../../contracts/work-mode';
import { useMutateWorkHomeMode, useQueryWorkHomeMode } from './useWorkHomeModeSetting';

interface UseWorkHomeModePersistenceArgs {
  workMode: WorkMode;
  composerMode: WorkMode;
  setWorkMode: (mode: WorkMode) => void;
  setComposerMode: (mode: WorkMode) => void;
  /** False while Research Preview terms are outstanding — no read/write. */
  canPersist: boolean;
}

/** Setting value while on Work chrome vs full Evidence landing. */
function modeToPersist(workMode: WorkMode, composerMode: WorkMode): WorkMode {
  return workMode === 'evidence' ? workMode : composerMode;
}

/**
 * Hydrates Work home from ``work-home_mode`` and writes back when that
 * destination changes. ``skipNextPersist`` is for session-local resets (New
 * chat, rotation, terms decline). ``persistMode`` saves a chosen destination
 * such as Home, including after a skip stamped the same mode without writing it.
 */
export function useWorkHomeModePersistence({
  workMode,
  composerMode,
  setWorkMode,
  setComposerMode,
  canPersist,
}: UseWorkHomeModePersistenceArgs) {
  const { data: persistedMode, isPending: isLoadingPersisted } =
    useQueryWorkHomeMode(canPersist);
  const { mutate: saveMode } = useMutateWorkHomeMode();

  const [hasHydrated, setHasHydrated] = useState(false);
  const lastSavedModeRef = useRef<WorkMode | null>(null);
  const skippedModeRef = useRef<WorkMode | null>(null);
  const workModeRef = useRef(workMode);
  const composerModeRef = useRef(composerMode);
  const canPersistRef = useRef(canPersist);
  const hasHydratedRef = useRef(hasHydrated);
  const saveModeRef = useRef(saveMode);
  workModeRef.current = workMode;
  composerModeRef.current = composerMode;
  canPersistRef.current = canPersist;
  hasHydratedRef.current = hasHydrated;
  saveModeRef.current = saveMode;

  const notifyNavigationOverride = useCallback(() => {
    hasHydratedRef.current = true;
    setHasHydrated(true);
  }, []);

  const skipNextPersist = useCallback((mode: WorkMode) => {
    lastSavedModeRef.current = mode;
    skippedModeRef.current = mode;
  }, []);

  // Writes immediately when the mode is already on screen. Otherwise leaves
  // the previous stamp so the effect that follows the state update writes once.
  const persistMode = useCallback((mode: WorkMode) => {
    if (lastSavedModeRef.current === mode && skippedModeRef.current !== mode) {
      return;
    }
    const current = modeToPersist(workModeRef.current, composerModeRef.current);
    if (current === mode && canPersistRef.current && hasHydratedRef.current) {
      lastSavedModeRef.current = mode;
      skippedModeRef.current = null;
      saveModeRef.current(mode);
      return;
    }
    if (lastSavedModeRef.current === mode) {
      lastSavedModeRef.current = null;
    }
  }, []);

  const isHydratingWorkHomeMode = canPersist && !hasHydrated && isLoadingPersisted;

  useLayoutEffect(() => {
    if (!canPersist || hasHydrated) {
      return;
    }
    if (isLoadingPersisted) {
      return;
    }
    hasHydratedRef.current = true;
    setHasHydrated(true);
    if (persistedMode !== 'work' && persistedMode !== 'evidence') {
      return;
    }
    lastSavedModeRef.current = persistedMode;
    setComposerMode(persistedMode);
    if (persistedMode === 'work') {
      setWorkMode('work');
    }
  }, [
    canPersist,
    hasHydrated,
    isLoadingPersisted,
    persistedMode,
    setComposerMode,
    setWorkMode,
  ]);

  const persistedTarget = modeToPersist(workMode, composerMode);

  useEffect(() => {
    if (!canPersist || !hasHydrated) {
      return;
    }
    if (lastSavedModeRef.current === persistedTarget) {
      return;
    }
    lastSavedModeRef.current = persistedTarget;
    skippedModeRef.current = null;
    saveMode(persistedTarget);
  }, [canPersist, hasHydrated, persistedTarget, saveMode]);

  return {
    notifyNavigationOverride,
    skipNextPersist,
    persistMode,
    isHydratingWorkHomeMode,
  };
}
